import { Controller } from "@hotwired/stimulus"

// Which circle a post is kept in, beside who reads it. The circle choice
// stands only while the post is for everyone: kept to the writer's circle or
// to them alone, a post is in none, and the choice goes back to none.
//
// Once the words have settled - a pause, enough of them to tell - the server
// is asked which circle they would sit well in, and the answer is offered
// beside the choice as a word to tap. Taken, it is chosen; left, it stays a
// suggestion. Nothing is chosen for the writer, and once they have chosen a
// circle (or none) by hand, nothing more is offered.
export default class extends Controller {
  static targets = ["audience", "choice", "circle", "hint", "body", "notebook"]
  static values = { url: String, perhaps: String, perhapsTitle: String }

  static SETTLE = 2500
  static MIN_WORDS = 25
  static MORE_WORDS = 30

  connect() {
    this.chosenByHand = this.hasCircleTarget && this.circleTarget.value !== ""
    this.askedAt = 0
  }

  disconnect() {
    clearTimeout(this.timer)
  }

  addressed() {
    // Kept to the writer alone, the notebook is offered beside the choice.
    if (this.hasNotebookTarget) this.notebookTarget.hidden = this.audienceTarget.value !== "only_me"
    if (!this.hasChoiceTarget) return

    const everyone = this.audienceTarget.value === "everyone"
    this.choiceTarget.hidden = !everyone
    if (!everyone) {
      this.choose("")
      this.hideHint()
    }
  }

  chosen() {
    if (this.choosing) return

    this.chosenByHand = true
    this.hideHint()
  }

  changed() {
    if (!this.urlValue || this.chosenByHand || !this.hasCircleTarget) return

    clearTimeout(this.timer)
    this.timer = setTimeout(() => this.settled(), this.constructor.SETTLE)
  }

  settled() {
    if (this.audienceTarget.value !== "everyone") return

    const words = this.bodyTarget.value.trim().split(/\s+/).filter(Boolean).length
    if (words < this.constructor.MIN_WORDS) return
    if (this.askedAt && words < this.askedAt + this.constructor.MORE_WORDS) return

    this.askedAt = words
    this.ask()
  }

  async ask() {
    try {
      const response = await fetch(this.urlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": this.csrfToken, "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ body: this.bodyTarget.value })
      })
      if (this.chosenByHand) return
      if (response.status === 204) return this.hideHint()
      if (!response.ok) return

      const { slug, name } = await response.json()
      if (!slug || this.circleTarget.value === slug) return this.hideHint()

      this.suggested = slug
      this.hintTarget.textContent = this.perhapsValue.replace("%{circle}", name)
      this.hintTarget.title = this.perhapsTitleValue.replace("%{circle}", name)
      this.hintTarget.hidden = false
    } catch {
      // Unreached, it offers nothing; the choice stands as it was.
    }
  }

  accept() {
    if (!this.suggested) return

    this.choose(this.suggested)
    this.chosenByHand = true
    this.hideHint()
  }

  choose(slug) {
    if (!this.hasCircleTarget || this.circleTarget.value === slug) return

    this.choosing = true
    this.circleTarget.value = slug
    this.circleTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.choosing = false
  }

  hideHint() {
    if (this.hasHintTarget) this.hintTarget.hidden = true
  }

  get csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.content ?? ""
  }
}
